/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from 'react';
import { useSignal } from '@preact/signals-react';
import { DropDownWrapper } from 'components';
import { useSelector, useDispatch } from 'react-redux';
import { DropDownArrowUp, DropDownArrowDown, ArrowDropDown } from 'assets/icons';
import { publisherReportsFiltersTableActions } from 'store/actions';

import * as S from './Grouping.styled';

const groupOptions = {
  applications: 'placements',
  date: null,
  country: 'placements',
  advertiser: null,
  placement: 'country',
};

const samsungGroupOptions = {
  date: null,
  country: null,
};

const getDefaultGrouping = (firstSelection, tableGrouping) => {
  if (firstSelection in tableGrouping) return tableGrouping[firstSelection];
  return null;
};

export const Grouping = ({
  HEADER_ROW_HEIGHT,
  STORAGE_KEY,
  currentRequestType$,
  originalTableData$,
  hideAdvertiserGrouping,
  isSamsungUser,
  sortConfig$,
  width,
}) => {
  const firstGroupRef = useRef(null);
  const secondGroupRef = useRef(null);
  const dispatch = useDispatch();
  const isFirstOpen$ = useSignal(false);
  const isSecondOpen$ = useSignal(false);
  const tableGrouping = useSelector((state) => state.publisherReportsFiltersTable.tableGrouping);

  const grouping = currentRequestType$.value?.split('_')[0];

  const [firstSelection, setFirstSelection] = useState(grouping);
  const [secondSelection, setSecondSelection] = useState(() =>
    getDefaultGrouping(firstSelection, tableGrouping),
  );

  const groupingList = isSamsungUser
    ? Object.keys(samsungGroupOptions)
    : hideAdvertiserGrouping
    ? Object.keys(groupOptions).filter((item) => item !== 'advertiser')
    : Object.keys(groupOptions);
  const secondOption = firstSelection ? groupOptions[firstSelection] : null;
  const isSecondOption = !!groupOptions[firstSelection];

  useEffect(() => {
    setFirstSelection(currentRequestType$.value.split('_')[0]);
  }, [currentRequestType$.value]);

  function handleFirstSelect(option) {
    if (currentRequestType$.value === option) {
      return;
    }
    setFirstSelection(option);
    currentRequestType$.value = option;
    originalTableData$.value = undefined;
    sortConfig$.value = null;
    saveLocalStorageItem({
      key: STORAGE_KEY,
      field: 'requestType',
      value: option,
    });
  }

  function handleSecondSelect(option) {
    let newOption = null;
    if (secondSelection !== option) {
      newOption = option;
    }
    setSecondSelection(newOption);
    dispatch(
      publisherReportsFiltersTableActions.updateTableGrouping({
        requestName: firstSelection,
        groupingValue: newOption,
      }),
    );
  }

  const requestSort = (key) => {
    let direction = 'ascending';
    if (
      sortConfig$.value &&
      sortConfig$.value.key === key &&
      sortConfig$.value.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    sortConfig$.value = { key, direction };
  };

  const getClassNamesFor = (name) => {
    if (!sortConfig$.value) return;
    return sortConfig$.value.key === name ? sortConfig$.value.direction : undefined;
  };

  return (
    <S.Box height={HEADER_ROW_HEIGHT} style={{ maxWidth: width, width: width }}>
      <p>Grouping:</p>
      <S.SelectWrapper
        isSelected={isFirstOpen$.value || firstSelection}
        onClick={() => (isFirstOpen$.value = !isFirstOpen$.value)}
        ref={firstGroupRef}
      >
        {firstSelection}
        {isFirstOpen$.value ? <DropDownArrowDown /> : <DropDownArrowUp />}
        {isFirstOpen$.value && (
          <DropDownWrapper
            isOpen$={isFirstOpen$}
            width={130}
            targetRef={firstGroupRef}
            style={{
              top: '50px',
              left: '0px',
            }}
            outSideClickAction={() => (isFirstOpen$.value = false)}
          >
            {groupingList.map((option) => (
              <S.Option
                key={option}
                isSelected={option === firstSelection}
                onClick={() => handleFirstSelect(option)}
              >
                {option}
              </S.Option>
            ))}
          </DropDownWrapper>
        )}
      </S.SelectWrapper>
      {isSecondOption && (
        <>
          <p>&</p>
          <S.SelectWrapper
            isSelected={isSecondOpen$.value || secondSelection}
            isDisabled={!secondOption}
            onClick={() => (isSecondOpen$.value = !isSecondOpen$.value)}
            ref={secondGroupRef}
          >
            {secondSelection ? secondSelection : <S.DefaultText>Choose grouping</S.DefaultText>}
            {isSecondOpen$.value ? <DropDownArrowDown /> : <DropDownArrowUp />}
            {isSecondOpen$.value && (
              <DropDownWrapper
                isOpen$={isSecondOpen$}
                width={160}
                targetRef={secondGroupRef}
                style={{
                  top: '50px',
                  left: '0px',
                }}
                outSideClickAction={() => (isSecondOpen$.value = false)}
              >
                {secondOption && (
                  <S.Option
                    onClick={() => handleSecondSelect(secondOption)}
                    isSelected={secondSelection}
                  >
                    {secondOption}
                  </S.Option>
                )}
              </DropDownWrapper>
            )}
          </S.SelectWrapper>
        </>
      )}
      <S.ArrowIcon
        direction={getClassNamesFor(currentRequestType$.value)}
        onClick={() => requestSort(currentRequestType$.value)}
      >
        <ArrowDropDown />
      </S.ArrowIcon>
    </S.Box>
  );
};

const getLocalStorageObj = ({ key }) => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : {};
  } catch (error) {
    console.error(error);
    return {};
  }
};

const saveLocalStorageItem = ({ key, field, value }) => {
  const objFromLS = getLocalStorageObj({ key });
  try {
    const updatedValue = { ...objFromLS, [field]: value };
    window.localStorage.setItem(key, JSON.stringify(updatedValue));
  } catch (error) {
    console.error(error);
  }
};
